import { App, Modal } from 'antd';
import ScoreHistoryTable from 'components/HistoryPredictions/ScoreHistoryTable';
import { SITE } from 'config/config';
import { useCallback, useEffect, useState } from 'react';
import { claimReward, historyPredictions } from 'service/base';
import 'styles/global.css';

const titleDecor = [
  { side: 'left', key: 'left-1' },
  { side: 'left', key: 'left-2' },
  { side: 'right', key: 'right-1' },
  { side: 'right', key: 'right-2' }
];

const HistoryPredictions = ({
  mode = 'modal',
  isModalVote = false,
  setIsModalVote,
  IP = '',
  FP = '',
  accountProp = '',
  activeHistoryTab = 'score',
  onScoreTabClick,
  onWorldCupTabClick
}) => {
  const { notification } = App.useApp();
  const [account, setAccount] = useState('');
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const handleCancelVote = () => {
    if (typeof setIsModalVote === 'function') {
      setIsModalVote(false);
    }
  };

  const fetchHistoryData = useCallback(
    async (accountName, { showEmptyWarning = true } = {}) => {
      const normalizedAccount = (accountName || '').trim();
      if (!normalizedAccount) {
        setRows([]);
        setCurrentPage(1);
        return;
      }

      setIsLoading(true);
      try {
        const payload = {
          account: normalizedAccount,
          site: SITE,
          ip: IP,
          fp: FP
        };
        const res = await historyPredictions(payload);
        const nextRows = Array.isArray(res?.data?.responseData) ? res.data.responseData : [];
        setRows(nextRows);
        setCurrentPage(1);

        if (showEmptyWarning && nextRows.length === 0) {
          notification.warning({
            message: 'Không có lịch sử',
            description: 'Không tìm thấy lịch sử bình chọn, vui lòng kiểm tra lại!'
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [FP, IP, notification]
  );

  useEffect(() => {
    if (mode !== 'page') return;

    const autoAccount = (
      accountProp ||
      localStorage.getItem('client_signin_username') ||
      localStorage.getItem('saved_account') ||
      ''
    ).trim();

    setAccount(autoAccount);
    fetchHistoryData(autoAccount, { showEmptyWarning: false });
  }, [accountProp, fetchHistoryData, mode]);

  const handleClaimReward = async (item) => {
    if (item.status_register !== 'apply') return;
    try {
      const accountValue = (account || item.account || '').trim().toLowerCase();
      const response = await claimReward({
        account: accountValue,
        site: SITE,
        matchId: item.matchId
      });
      if (response?.data?.code === 0 || response?.code === 0) {
        notification.success({ message: 'Thành công', description: 'Nhận thưởng thành công!' });
        await fetchHistoryData(accountValue, { showEmptyWarning: false });
      } else {
        notification.error({ message: 'Nhận thưởng thất bại!', description: response?.data?.message || 'Vui lòng thử lại.' });
      }
    } catch (error) {
      notification.error({ message: 'Nhận thưởng thất bại!', description: error.message });
    }
  };

  const contentNode = (
    <div className={`content-km content-history ${mode === 'page' ? 'history-page-skin history-page-unify' : ''}`}>
      {mode === 'page' ? (
        <div className="mb-3 md:mb-4 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => onScoreTabClick?.()}
            className={`h-[30px] md:h-[38px] min-w-[108px] md:min-w-[180px] rounded-[8px] border-none appearance-none px-3 md:px-4 text-[11px] md:text-[20px] font-bold text-white outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 ${
              activeHistoryTab === 'score'
                ? 'bg-gradient-to-b from-[#00CD32] to-[#048724]'
                : 'bg-[#9ea3a7]'
            }`}
          >
            Dự đoán tỉ số
          </button>

          <div
            className={`rounded-[9px] p-[1px] md:rounded-[11px] md:p-[2px] ${
              activeHistoryTab === 'worldcup'
                ? 'bg-gradient-to-b from-[#006719] to-[#2CF65D]'
                : 'bg-[#9ea3a7]'
            }`}
          >
            <button
              type="button"
              onClick={() => onWorldCupTabClick?.()}
              className={`h-[30px] md:h-[38px] min-w-[108px] md:min-w-[180px] rounded-[8px] border-none appearance-none px-3 md:px-4 text-[11px] md:text-[20px] font-bold text-white outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 ${
                activeHistoryTab === 'worldcup'
                  ? 'bg-gradient-to-b from-[#00CD32] to-[#048724]'
                  : 'bg-[#9ea3a7]'
              }`}
            >
              Dự đoán World Cup
            </button>
          </div>
        </div>
      ) : null}

      <div className="list-history">
        <ScoreHistoryTable
          data={rows}
          isLoading={isLoading}
          page={currentPage}
          onPageChange={setCurrentPage}
          onClaimReward={handleClaimReward}
        />
      </div>

      {mode !== 'modal' ? (
        <p className="mt-3 text-center text-[13px] md:text-[16px] font-semibold text-[#0f6b2c]">
          Tài khoản: {account || '-'}
        </p>
      ) : null}
    </div>
  );

  if (mode === 'page') {
    return (
      <section className="mx-auto mt-4 w-full max-w-[1236px] history-page-shell">
        <div className="rounded-[24px] border-[3px] border-[#006719] bg-[#d7e6da] p-3 md:p-5">
          <div className="mx-auto mb-3 flex w-fit items-center justify-center gap-2 md:mb-4">
            {titleDecor
              .filter((item) => item.side === 'left')
              .map((item) => (
                <span
                  key={item.key}
                  className="h-7 w-[6px] skew-x-[-12deg] rounded-full bg-gradient-to-b from-[#17d147] to-[#0e7e2c] md:h-8"
                />
              ))}

            <div className="h-[34px] min-w-[210px] rounded-[8px] border border-[#00a130] bg-gradient-to-b from-[#00c838] to-[#008f28] px-5 text-center text-[18px] md:text-[22px] font-bold uppercase text-[#f5fffb] md:min-w-[250px] md:px-8">
              LỊCH SỬ
            </div>

            {titleDecor
              .filter((item) => item.side === 'right')
              .map((item) => (
                <span
                  key={item.key}
                  className="h-7 w-[6px] skew-x-[-12deg] rounded-full bg-gradient-to-b from-[#17d147] to-[#0e7e2c] md:h-8"
                />
              ))}
          </div>

          {contentNode}
        </div>
      </section>
    );
  }

  return (
    <Modal
      className="modal-jun88k36 modal-history"
      title="DANH SÁCH LỊCH SỬ DỰ ĐOÁN"
      open={isModalVote}
      footer={null}
      onCancel={handleCancelVote}
    >
      {contentNode}
    </Modal>
  );
};

export default HistoryPredictions;
